<template>
  <template v-if="areasVisible">
    <net-area
      v-for="area in areas"
      :id="area.id"
      :key="'area_' + area.id"
      @select="emit('select-item', $event)"
    />
  </template>

  <template v-if="placeholdersVisible">
    <net-target-placeholder
      v-for="position in templatePositions"
      :key="position.id"
      :position="position"
      @contextmenu="onContextMenu($event)"
    />
  </template>

  <net-index-arrows v-if="sceneTree.activeScene && sceneTree.activeScene.indexConnectionsVisible" />

  <net-relation-manager
    :clickable="!node.superLocked"
    :net-mode="mode"
    :relations="relations"
    :flow-branches="flowBranches"
    :element-values="elementValues"
    :itemSizes="itemSizes"
    :selection="selection"
    :fade="fadeRelations"
    :show-selected-net-item-connections="
      showSelectedNetItemConnections &&
      activeMode.showSelectedNetItemConnections
    "
    :last-element-size-change="lastElementSizeChange"

    :flow-source-element="flowSourceElement"
    :impact-source-element="impactSourceElement"
    :relation-draft="relationDraft"
    :element-at-mouse-position="elementNodeAtMouse"
    @select="emit('select-item', $event)"
    @add-select="emit('add-select-item', $event)"
    @contextmenu="onContextMenu($event)"
  />

  <net-flow-anchor
    v-for="flow in visibleFlows"
    :uid="flow.uid"
    :selected="selection.selected.has(flow.uid)"
    :selection="selection"
    :editable="mode === 'edit'"
    :clickable="!node.superLocked"
    @select="emit('select-item', $event)"
    @contextmenu="onContextMenu($event)"
  />
</template>

<script setup lang="ts">
import { computed, toRefs, inject } from 'vue';
import { keyNetCanvas } from '@editor/net/netCanvasInjectionKeys';
import NetArea from '../area/NetArea.vue';
import NetTargetPlaceholder from '../target/NetTargetPlaceholder.vue';
import NetIndexArrows from '../index/NetIndexArrows.vue';
import NetFlowAnchor from '../flow/NetFlowAnchor.vue';
import NetRelationManager from '../relation/NetRelationManager.vue';

import { useAreaStore } from '../area/areas.store';
import { useTemplateStore } from '../template/templates.store';
import { useSceneTree } from '../scene/sceneTree';

import type { NetCanvasProps } from '@editor/net/NetCanvasProps';
import type { Uid } from '@/helpers/utility/Id';
import type { SceneTreeItemSize, SceneTreeNode } from '../scene/SceneTreeItem';

const injectedCanvas = inject(keyNetCanvas);
const canvasProps = computed((): NetCanvasProps => injectedCanvas?.value.props as NetCanvasProps || {} as NetCanvasProps);

const {
  flowSourceElement,
  impactSourceElement,
  selection,
  elementValues,
  fadeRelations,
  relations,
  flowBranches,
  relationDraft,
  showSelectedNetItemConnections,
  mode
} = toRefs(canvasProps.value);

const props = defineProps<{
  node: SceneTreeNode;
  itemSizes: { [uid: Uid<"sceneNode" | "flow">]: SceneTreeItemSize },
  lastElementSizeChange: number;
  elementNodeAtMouse: SceneTreeItemSize<"element"> | null;
}>();

const emit = defineEmits<{
  (e: "contextmenu", event: MouseEvent & { item?: { uid: Uid<"relation" | "flowBranch" | "elementPlaceholder"> } | null, path: Uid<"sceneNode">[] }): void;
  (e: "click", event: MouseEvent): void;
  (e: "toggle-relation-mode", item: { uid: Uid<"target" | "resource" | "index"> }): void;
  (e: "toggle-flow-mode", item: { uid: Uid<"target" | "resource"> }): void;
  (e: "select-item", item: { uid: Uid<"relation" | "flow" | "flowBranch" | "area">; }): void;
  (e: "add-select-item", item: { uid: Uid<"relation" | "flow" | "flowBranch" | "area">; }): void;
  (e: "select-many", items: { uid: Uid<"relation" | "flow" | "flowBranch" | "area">; }[]): void;
  (e: "clear-all-selection"): void;
  (e: "delete"): void;
  (e: "wheel", event: WheelEvent): void;
  (e: "update:tool", tool: NetCanvasProps["tool"]): void;
  (e: "update:element-value", payload: { uid: Uid<"target" | "resource" | "index">; value: number }): void;
}>();

const sceneTree = useSceneTree();

const MODES = Object.freeze({
  edit: {
    allowElementTransforms: true,
    allowRelationCreation: true,
    showPlaceholders: true,
    showCheckboxes: false,
    showSelectedNetItemConnections: true
  },
  view: {
    allowElementTransforms: false,
    allowRelationCreation: false,
    showPlaceholders: false,
    showCheckboxes: false,
    showSelectedNetItemConnections: true
  },
  choose: {
    allowElementTransforms: false,
    allowRelationCreation: false,
    showPlaceholders: false,
    showCheckboxes: true,
    showSelectedNetItemConnections: false
  },
  influence: {
    allowElementTransforms: false,
    allowRelationCreation: false,
    showPlaceholders: false,
    showCheckboxes: false,
    showSelectedNetItemConnections: true
  }
});
const activeMode = computed(() => {
  return MODES[mode.value];
});

// ================================
// AREAS 
const areaStore = useAreaStore();

const areasVisible = computed(() => {
  return sceneTree.activeScene?.areasVisible ?? true;
});
const areas = computed(() => {
  return areaStore.orderedItems.filter(area => area.sceneId === sceneTree.activeScene?.id);
});

// =================================
// FLOWS

const visibleFlows = computed(() => {
  const flows = sceneTree.visibleFlows;
  return flows.filter((flow) => {
    if (!props.itemSizes[flow.uid]) return false;
    return true;
  });
});

// =================================
// TEMPLATES

const templateStore = useTemplateStore();

const template = computed(() => templateStore.template);
const placeholdersVisible = computed(() => {
  const activeScene = sceneTree.activeScene;
  if (!activeScene) return false;
  if (activeScene.placeholdersVisible === false) return false;
  return activeMode.value.showPlaceholders;
});
const templatePositions = computed(() => {
  if (template.value == null) return {};
  return template.value.targetPositions;
});

// ================================
// INTERACTION

function onContextMenu(event: MouseEvent & { item?: { uid: Uid<"relation" | "flowBranch" | "elementPlaceholder"> } | null, path?: Uid<"sceneNode">[] }) {
  if (!event.path) event.path = [...props.node.path];
  emit("contextmenu", event as MouseEvent & { item?: { uid: Uid<"relation" | "flowBranch" | "elementPlaceholder"> } | null, path: Uid<"sceneNode">[] });
}
</script>